import React, { useContext, useEffect, useMemo, useState, useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { AuthContext } from "../../../context/Auth/AuthContext";
import { ROLES } from "../../../constants/roles";
import api from "../../../services/api";
import { toast } from "react-toastify";
import toastError from "../../../errors/toastError";
import { i18n } from "../../../translate/i18n";
import MasterPageBreadcrumb from "../../../layout/master/MasterPageBreadcrumb";
import { getMasterRouteMeta } from "../../../layout/master/masterRouteMeta";
import MasterSpinner from "../../../components/Master/MasterSpinner";
import ViewCompanyModal from "../../../components/Master/ViewCompanyModal";
import ContractDetailsModal from "../../../components/Master/ContractDetailsModal";
import ContractDueDateBadge from "../../../components/Master/ContractDueDateBadge";
import ContractValueBadge from "../../../components/Master/ContractValueBadge";
import { isCompanyActive } from "../../../helpers/companyActive";
import { isMasterCompany } from "../../../helpers/masterUser";

const PER_PAGE = 10;

function IconSearch() {
	return (
		<svg className="size-3.5 text-default-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<circle cx="11" cy="11" r="8" />
			<path d="m21 21-4.35-4.35" />
		</svg>
	);
}

function IconCheck() {
	return (
		<svg className="size-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
			<polyline points="22 4 12 14.01 9 11.01" />
		</svg>
	);
}

function IconX() {
	return (
		<svg className="size-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<circle cx="12" cy="12" r="10" />
			<line x1="15" y1="9" x2="9" y2="15" />
			<line x1="9" y1="9" x2="15" y2="15" />
		</svg>
	);
}

function IconEye() {
	return (
		<svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
			<circle cx="12" cy="12" r="3" />
		</svg>
	);
}

function IconDocument() {
	return (
		<svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
			<polyline points="14 2 14 8 20 8" />
			<line x1="16" y1="13" x2="8" y2="13" />
			<line x1="16" y1="17" x2="8" y2="17" />
		</svg>
	);
}

function IconEdit() {
	return (
		<svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
			<path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
		</svg>
	);
}

function IconChevronLeft() {
	return (
		<svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<polyline points="15 18 9 12 15 6" />
		</svg>
	);
}

function IconChevronRight() {
	return (
		<svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<polyline points="9 18 15 12 9 6" />
		</svg>
	);
}

function companyInitials(name) {
	if (!name) return "?";
	const parts = String(name).trim().split(/\s+/).filter(Boolean);
	if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
	return (parts[0][0] + parts[1][0]).toUpperCase();
}

export default function MasterCompanies() {
	const history = useHistory();
	const location = useLocation();
	const { user, loading } = useContext(AuthContext);
	const [companies, setCompanies] = useState([]);
	const [loadingCompanies, setLoadingCompanies] = useState(true);
	const [viewCompany, setViewCompany] = useState(null);
	const [contractDetailsCompany, setContractDetailsCompany] = useState(null);
	const [togglingActiveId, setTogglingActiveId] = useState(null);
	const [currentPage, setCurrentPage] = useState(1);
	const [search, setSearch] = useState("");
	const [statusFilter, setStatusFilter] = useState("");
	const routeMeta = getMasterRouteMeta("/admin-master/companies");

	const filteredCompanies = useMemo(() => {
		let list = companies;
		const q = search.trim().toLowerCase();
		if (q) {
			list = list.filter((c) => {
				const name = (c.name || "").toLowerCase();
				const doc = String(c.document || "").replace(/\D/g, "");
				const id = String(c.id || "");
				return name.includes(q) || doc.includes(q.replace(/\D/g, "")) || id.includes(q);
			});
		}
		if (statusFilter === "active") {
			list = list.filter((c) => isCompanyActive(c.isActive));
		} else if (statusFilter === "inactive") {
			list = list.filter((c) => !isCompanyActive(c.isActive));
		}
		return list;
	}, [companies, search, statusFilter]);

	const totalPages = Math.max(1, Math.ceil(filteredCompanies.length / PER_PAGE));
	const start = (currentPage - 1) * PER_PAGE;
	const paginatedCompanies = filteredCompanies.slice(start, start + PER_PAGE);

	useEffect(() => {
		setCurrentPage(1);
	}, [search, statusFilter]);

	useEffect(() => {
		if (currentPage > totalPages) setCurrentPage(totalPages);
	}, [filteredCompanies.length, currentPage, totalPages]);

	const handleViewCompany = (company) => {
		api
			.get(`/master/companies/${company.id}`)
			.then(({ data }) => setViewCompany(data))
			.catch((err) => {
				toastError(err);
				setViewCompany(null);
			});
	};

	const copyToClipboard = (text, label) => {
		if (!text || text === "—") return;
		navigator.clipboard.writeText(text).then(() => {
			toast.success(`${label || "Copiado"} para a área de transferência.`);
		}).catch(() => toast.error("Não foi possível copiar."));
	};

	const handleViewContractDetails = (company) => {
		setContractDetailsCompany(company);
	};

	const handleEditCompany = (company) => {
		history.push(`/admin-master/companies/${company.id}/edit`);
	};

	const handleToggleActive = (company) => {
		if (isMasterCompany(company)) {
			toast.warning(i18n.t("master.pages.masterCompanyLocked"));
			return;
		}
		const newActive = !isCompanyActive(company.isActive);
		setTogglingActiveId(company.id);
		api
			.put(`/master/companies/${company.id}`, { isActive: newActive })
			.then(() => {
				toast.success(newActive ? "Empresa ativada." : "Empresa inativada.");
				setCompanies((prev) =>
					prev.map((c) => (c.id === company.id ? { ...c, isActive: newActive } : c))
				);
				if (viewCompany && viewCompany.id === company.id) {
					setViewCompany((prev) => (prev ? { ...prev, isActive: newActive } : null));
				}
			})
			.catch((err) => toastError(err))
			.finally(() => setTogglingActiveId(null));
	};

	const fetchCompanies = useCallback(() => {
		setLoadingCompanies(true);
		return api
			.get("/master/companies")
			.then(({ data }) => setCompanies(data || []))
			.catch((err) => toastError(err))
			.finally(() => setLoadingCompanies(false));
	}, []);

	useEffect(() => {
		if (loading) return;
		if (!user || user.profile !== ROLES.master) {
			history.replace("/");
			return;
		}
		fetchCompanies();
	}, [user, loading, history, location.pathname, fetchCompanies]);

	if (loading || !user || user.profile !== ROLES.master) {
		return null;
	}

	const formatDate = (dateStr) => {
		if (!dateStr) return "—";
		const d = new Date(dateStr);
		return d.toLocaleDateString("pt-BR", {
			day: "2-digit",
			month: "short",
			year: "numeric",
		});
	};

	const formatCnpj = (value) => {
		if (value == null || value === "") return "—";
		const digits = String(value).replace(/\D/g, "");
		if (digits.length !== 14) return value;
		return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
	};

	const pageNumbers = () => {
		const pages = [];
		const max = 5;
		let from = Math.max(1, currentPage - 2);
		let to = Math.min(totalPages, from + max - 1);
		from = Math.max(1, to - max + 1);
		for (let p = from; p <= to; p += 1) pages.push(p);
		return pages;
	};

	return (
		<div className="mx-auto w-full max-w-none box-border">
			<MasterPageBreadcrumb
				title={i18n.t(routeMeta.titleKey)}
				breadcrumbs={routeMeta.breadcrumbs}
			/>

			<div className="card">
				<div className="card-header">
					<h6 className="card-title m-0">{i18n.t("master.pages.companiesListTitle")}</h6>
					<span className="text-sm text-default-500">
						{filteredCompanies.length} {i18n.t("master.pages.companiesCountLabel")}
					</span>
				</div>

				<div className="card-body border-b border-default-200 pb-6 dark:border-default-700">
					<div className="flex flex-col gap-4 md:flex-row md:items-center">
						<div className="relative">
							<input
								type="search"
								className="form-input form-input-sm w-full min-w-[220px] ps-9 md:w-64"
								placeholder={i18n.t("master.pages.companiesSearchPlaceholder")}
								value={search}
								onChange={(e) => setSearch(e.target.value)}
							/>
							<div className="pointer-events-none absolute inset-y-0 start-0 flex items-center ps-3">
								<IconSearch />
							</div>
						</div>
						<select
							className="form-select form-input-sm min-w-[140px]"
							value={statusFilter}
							onChange={(e) => setStatusFilter(e.target.value)}
						>
							<option value="">{i18n.t("master.pages.companiesFilterAll")}</option>
							<option value="active">{i18n.t("master.pages.statusActive")}</option>
							<option value="inactive">{i18n.t("master.pages.statusInactive")}</option>
						</select>
					</div>
				</div>

				{loadingCompanies ? (
					<div className="flex flex-col items-center justify-center gap-4 px-6 pb-8 pt-6 text-default-500">
						<MasterSpinner />
						<span>{i18n.t("master.pages.loadingCompanies")}</span>
					</div>
				) : filteredCompanies.length === 0 ? (
					<div className="flex flex-col items-center justify-center gap-2 px-6 pb-8 pt-6 text-center">
						<p className="m-0 font-semibold text-default-800 dark:text-default-100">
							{i18n.t("master.pages.noCompanies")}
						</p>
						<p className="m-0 text-sm text-default-500">{i18n.t("master.pages.noCompaniesHint")}</p>
					</div>
				) : (
					<div className="flex flex-col pt-5">
						<div className="w-full">
							<div className="w-full align-middle">
								<div className="w-full">
									<table className="w-full table-auto divide-y divide-default-200 dark:divide-default-700">
										<thead className="bg-default-150 dark:bg-default-800/60">
											<tr className="whitespace-nowrap text-sm font-normal text-default-700 dark:text-default-300">
												<th className="ps-4 text-start">
													<input
														type="checkbox"
														className="form-checkbox"
														disabled
														aria-hidden
														tabIndex={-1}
													/>
												</th>
												<th className="px-3.5 py-3 text-start">ID</th>
												<th className="px-3.5 py-3 text-start">
													{i18n.t("master.pages.colCompany")}
												</th>
												<th className="px-3.5 py-3 text-start min-w-[120px]">
													{i18n.t("master.pages.colContractDueDate")}
												</th>
												<th className="px-3.5 py-3 text-start min-w-[110px]">
													{i18n.t("master.pages.colContractValue")}
												</th>
												<th className="px-3.5 py-3 text-start">
													{i18n.t("master.pages.colUsers")}
												</th>
												<th className="px-3.5 py-3 text-start">
													{i18n.t("master.pages.colLimit")}
												</th>
												<th className="px-3.5 py-3 text-start">
													{i18n.t("master.pages.colConnections")}
												</th>
												<th className="px-3.5 py-3 text-start">
													{i18n.t("master.pages.colStatus")}
												</th>
												<th className="px-3.5 py-3 text-start">
													{i18n.t("master.pages.colCreated")}
												</th>
												<th className="px-3.5 py-3 text-start">
													{i18n.t("master.pages.colActions")}
												</th>
											</tr>
										</thead>
										<tbody className="divide-y divide-default-200 dark:divide-default-700">
											{paginatedCompanies.map((company) => {
												const isActive = isCompanyActive(company.isActive);
												const isSystemCompany = isMasterCompany(company);
												return (
													<tr
														key={company.id}
														className="whitespace-nowrap text-sm font-normal text-default-800 dark:text-default-200"
													>
														<td className="py-3 ps-4">
															<input type="checkbox" className="form-checkbox" aria-label={`#${company.id}`} />
														</td>
														<td className="px-3.5 py-3 text-primary font-medium">
															#{company.id}
														</td>
														<td className="px-3.5 py-3">
															<div className="flex items-center gap-3 whitespace-nowrap">
																<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-default-200 text-sm font-semibold text-default-700 dark:bg-default-700 dark:text-default-200">
																	{companyInitials(company.name)}
																</div>
																<div className="min-w-0">
																	<h6 className="mb-0.5 font-semibold text-default-800 dark:text-default-100">
																		<button
																			type="button"
																			className="cursor-pointer border-0 bg-transparent p-0 text-left text-inherit hover:text-primary"
																			onClick={() => handleViewCompany(company)}
																		>
																			{company.name || "—"}
																		</button>
																	</h6>
																	<p className="m-0 text-default-500">
																		{formatCnpj(company.document)}
																	</p>
																</div>
															</div>
														</td>
														<td className="px-3.5 py-3">
															<ContractDueDateBadge value={company.contractDueDate} />
														</td>
														<td className="px-3.5 py-3">
															<ContractValueBadge value={company.contractValue} />
														</td>
														<td className="px-3.5 py-3">{company.userCount ?? 0}</td>
														<td className="px-3.5 py-3">{company.userLimit ?? "—"}</td>
														<td className="px-3.5 py-3">{company.connectionCount ?? 0}</td>
														<td className="px-3.5 py-3">
															<div className="flex items-center gap-3">
																{isActive ? (
																	<span className="inline-flex items-center gap-x-1 rounded px-2.5 py-0.5 text-xs font-medium bg-success/10 text-success">
																		<IconCheck />
																		{i18n.t("master.pages.statusActive")}
																	</span>
																) : (
																	<span className="inline-flex items-center gap-x-1 rounded px-2.5 py-0.5 text-xs font-medium bg-danger/10 text-danger">
																		<IconX />
																		{i18n.t("master.pages.statusInactive")}
																	</span>
																)}
																{isSystemCompany ? (
																	<span
																		className="text-xs text-default-500"
																		title={i18n.t("master.pages.masterCompanyLocked")}
																	>
																		{i18n.t("master.pages.masterCompanyBadge")}
																	</span>
																) : (
																	<button
																		type="button"
																		className={`master-toggle ${
																			isActive ? "master-toggle--on" : ""
																		}`}
																		title={
																			isActive
																				? i18n.t("master.pages.deactivate")
																				: i18n.t("master.pages.activate")
																		}
																		aria-label={
																			isActive
																				? i18n.t("master.pages.deactivate")
																				: i18n.t("master.pages.activate")
																		}
																		aria-pressed={isActive}
																		disabled={togglingActiveId === company.id}
																		onClick={() => handleToggleActive(company)}
																	>
																		<span className="master-toggle__thumb" />
																	</button>
																)}
															</div>
														</td>
														<td className="px-3.5 py-3 text-default-600 dark:text-default-400">
															{formatDate(company.createdAt)}
														</td>
														<td className="px-3.5 py-3">
															<div className="flex items-center gap-1">
																<button
																	type="button"
																	className="btn size-7 bg-default-200 text-default-500 hover:border-primary/10 hover:bg-primary/10 hover:text-primary dark:bg-default-700 dark:hover:bg-primary/20"
																	title={i18n.t("master.pages.viewCompany")}
																	onClick={() => handleViewCompany(company)}
																>
																	<IconEye />
																</button>
																<button
																	type="button"
																	className="btn size-7 bg-default-200 text-default-500 hover:border-primary/10 hover:bg-primary/10 hover:text-primary dark:bg-default-700 dark:hover:bg-primary/20"
																	title={i18n.t("master.pages.viewContractDetails")}
																	onClick={() => handleViewContractDetails(company)}
																>
																	<IconDocument />
																</button>
																<button
																	type="button"
																	className="btn size-7 bg-default-200 text-default-500 hover:border-primary/10 hover:bg-primary/10 hover:text-primary dark:bg-default-700 dark:hover:bg-primary/20"
																	title={i18n.t("master.pages.editCompany")}
																	onClick={() => handleEditCompany(company)}
																>
																	<IconEdit />
																</button>
															</div>
														</td>
													</tr>
												);
											})}
										</tbody>
									</table>
								</div>
							</div>
						</div>

						<div className="card-footer">
							<p className="m-0 text-sm text-default-500">
								{i18n.t("master.pages.companiesShowing", {
									start: filteredCompanies.length ? start + 1 : 0,
									end: Math.min(start + PER_PAGE, filteredCompanies.length),
									total: filteredCompanies.length,
								})}
							</p>
							<nav className="flex flex-wrap items-center gap-2" aria-label="Pagination">
								<button
									type="button"
									className="btn btn-sm border border-default-200 bg-transparent text-default-600 hover:border-primary/10 hover:bg-primary/10 hover:text-primary dark:border-default-600 dark:text-default-300"
									disabled={currentPage <= 1}
									onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
								>
									<IconChevronLeft />
									<span className="ms-1">{i18n.t("master.pages.previous")}</span>
								</button>
								{pageNumbers().map((p) => (
									<button
										key={p}
										type="button"
										className={`btn size-7 border ${
											p === currentPage
												? "border-primary bg-primary text-white"
												: "border-default-200 bg-transparent text-default-600 hover:border-primary/10 hover:bg-primary/10 hover:text-primary dark:border-default-600 dark:text-default-300"
										}`}
										onClick={() => setCurrentPage(p)}
									>
										{p}
									</button>
								))}
								<button
									type="button"
									className="btn btn-sm border border-default-200 bg-transparent text-default-600 hover:border-primary/10 hover:bg-primary/10 hover:text-primary dark:border-default-600 dark:text-default-300"
									disabled={currentPage >= totalPages}
									onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
								>
									<span className="me-1">{i18n.t("master.pages.next")}</span>
									<IconChevronRight />
								</button>
							</nav>
						</div>
					</div>
				)}
			</div>

			<ViewCompanyModal
				company={viewCompany}
				onClose={() => setViewCompany(null)}
				formatCnpj={formatCnpj}
				formatDate={formatDate}
				onCopy={copyToClipboard}
			/>

			<ContractDetailsModal
				company={contractDetailsCompany}
				onClose={() => setContractDetailsCompany(null)}
			/>
		</div>
	);
}
